import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanDeactivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap, first } from 'rxjs/operators';
import { Authorize, GetProfile, VerifySSO } from './core/states/store.actions';
import { StoreState } from './core/states/store.state';
import { StoreService } from './store.service';
import { UtilitiesService } from './core/utilities.service';
import { DealerService } from 'src/app/dealer.service';
import { DataService } from './data.service';
import { StoreInfoService } from 'src/app/store-info.service';
import { LoadingbarService } from 'src/app/loadingbar.service';
@Injectable({ providedIn: 'root' })
export class RouteGuard implements CanActivate, CanActivateChild {
  constructor(
    private store: Store,
    private svc: StoreService,
    private router: Router,
    private utils: UtilitiesService,
    private dataService: DataService,
    private dealerService: DealerService,
    private storeInfoService: StoreInfoService,
    private loading: LoadingbarService
  ) {}
  dealer$ = this.dealerService.getDealer();
  dealer: any = [];
  defaultStore;
  defaultData;
  canInDashboard = false;
  storeList$ = this.storeInfoService.getStoreList();
  storeListInfo;
  // Store requested by the SSO link (e.g. from a notification); overrides the dealer's default store.
  targetStoreId = null;
  targetStore = null;

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const isValidated = this.store.selectSnapshot(StoreState.validated);

    if (isValidated) {
      return of(true);
    }

    if (state.root.queryParams) {
      const { token, redirect_to, from_store, store_id } = state.root.queryParams;
      let fromStore;

      if (token) {
        fromStore = typeof from_store !== 'undefined';
        this.targetStoreId = store_id ?? null;

        const source = this.store
          .dispatch(new VerifySSO({ token, redirect_to, fromStore }))
          .pipe(
            catchError((err) => of(true)),
            mergeMap((_) =>
              this.store.select((state) => state.store.validated)
            ),
            mergeMap((validated) => {
              if (!validated) {
                this.router.navigate(['access-denied'], {
                  skipLocationChange: true,
                });
              } else {
                console.log('skipLocationChange check');
                localStorage.removeItem('bearer');
                this.signOn();
              }
              return of(validated);
            })
          );
        this.loading.updateLoadingBar(false);
        return source;
      } else if (this.svc.userInfo.token) {
        this.storeInfoService.fetchStoreList();
        return this.store
          .dispatch([
            new Authorize(true),
            //new GetProfile(this.svc.userInfo.d_id)
          ])
          .pipe(
            catchError((err) => of(true)),
            mergeMap((_) => {
              this.storeList$.pipe(first()).subscribe((res) => {
                this.dataService.emitStoreListUpdatedEvent(res);
                
                const isSSLoginUrl = state.url.indexOf('/sso/login') !== -1;
                
                if (res.length > 0) {
                  if (isSSLoginUrl) {
                    this.router.navigate(['/dashboard/overview']);
                  }
                  this.storeInfoService.fetchStoreInfo();
                  this.dealerService.fetchDealerInfo();
                } else if (isSSLoginUrl) {
                  this.router.navigate(['homepage']);
                }
              });
              
              this.loading.updateLoadingBar(false);
              return of(true);
            })
          );
      }
    }
    this.loading.updateLoadingBar(false);
    this.router.navigate(['access-denied'], { skipLocationChange: true });
    return of(false);
  }

  signOn() {
    let info = localStorage.getItem('5s-info');
    let token = '';
    // console.log('check info data ', info);
    if (window.location.search) {
      let queryStr = window.location.search;
      queryStr = queryStr.substr(1);
      let queryArr = queryStr.split('&');
      queryArr.forEach((value) => {
        let params = value.split('=');
        if (params[0] === 'token') {
          token = params[1];
        }
      });
    }

    if (token === '' && info) {
      // Not from Login
      let parse = JSON.parse(info);
      token = parse.token;
    }

    if (token) {
      // console.log('token', localStorage.getItem('bearer'));
      this.svc.signOn(token).subscribe(
        (res) => {
          localStorage.setItem('bearer', res.data);
          this.getDefaultStore().then(() => {
            // this.svc
            //   // .getLatestSummary(token,this.defaultStore ?? localStorage.getItem('store_id'))
            //   // .subscribe((res2) => {});

            console.log('this.defaultStore', this.defaultStore);

            if (
              this.defaultStore !== null &&
              this.defaultStore !== undefined &&
              this.defaultStore !== 0
            ) {
              const storeId =
                localStorage.getItem('store_id') ?? this.defaultStore;
              this.storeInfoService.fetchStoreInfo();
              this.storeInfoService.fetchStoreList();
              this.storeInfoService.getStoreInfos().subscribe((dealerInfo) => {
                const url = dealerInfo?.config?.partner_platform_url;
                const dealerPlan = this.dataService.getDealerPlan();
                if (dealerPlan.plan_name?.toLowerCase() === 'none') {
                  this.utils.errorNoPlan(url + '#/acl-subscription-payment');
                } else {
                  this.getStoreData(res.data, url).finally(() => {});
                }
              });
            }
          });
        },
        (err) => {
          if (
            err?.error?.message === 'Expired token' ||
            +err?.error?.status_code === 400
          ) {
            console.log('expired token');
            this.logoutUser();
          }
        }
      );
    }
  }

  async getDefaultStore() {
    try {
      await this.dealerService.fetchDealerInfo();

      localStorage.removeItem('store_id');

      const res = await this.dealer$.pipe(first()).toPromise();
      this.dealer = res;

      this.defaultStore = this.dealer?.default_store;

      if (this.defaultStore) {
        localStorage.setItem('default_store', this.defaultStore);

        this.targetStore = await this.resolveTargetStore();
        localStorage.setItem(
          'store_id',
          this.targetStore?.store_id?.toString() ?? this.defaultStore
        );
      } else {
        this.loading.updateLoadingBar(false);
        this.router.navigate(['homepage']);
        return false;
      }

      this.dataService.updateDealerInfo(res);
      this.dataService.updateDealerPlan(res['dealer_plan']);
      this.dataService.updateDealerCredits(res['dealer_credits']);

      return true;
    } catch (error) {
      console.error('Error in getDefaultStore:', error);
      this.loading.updateLoadingBar(false);
      // this.logoutUser();
    }
    // return new Promise((resolve, reject) => {
    //   this.dealer$.subscribe(dealerData => {
    //     if (dealerData) {
    //       this.dealer = dealerData;
    //       this.defaultStore = this.dealer?.default_store;
    //       console.log('default store1231313', this.defaultStore);
    //       if (this.defaultStore) {
    //         localStorage.setItem('default_store', this.defaultStore);
    //         if (!localStorage.getItem('store_id')) {
    //           localStorage.setItem('store_id', this.defaultStore);
    //         }
    //       }else{
    //         this.loading.updateLoadingBar(false);
    //         this.router.navigate(['homepage']);
    //       }
    //       this.dataService.updateDealerInfo(dealerData);
    //       this.dataService.updateDealerPlan(dealerData.dealer_plan);
    //       this.dataService.updateDealerCredits(dealerData.dealer_credits);
    //     }setTimeout(() => {
    //       resolve(true);
    //     }, 1);
    //   });
    //     (error) => {
    //       console.log('error here')
    //       this.logoutUser();
    //       reject(error);
    //     };
    // });
  }

  // Only honour the requested store when it is one of this dealer's active stores.
  async resolveTargetStore() {
    const storeId = this.targetStoreId;
    this.targetStoreId = null;

    if (!storeId) {
      return null;
    }

    try {
      const res = await this.svc
        .getStoreLists(localStorage.getItem('bearer'))
        .pipe(first())
        .toPromise();

      return (
        Object.values(res?.['data'] ?? {}).find(
          (store: any) => +store.store_id === +storeId
        ) ?? null
      );
    } catch (error) {
      console.error('Error resolving target store:', error);
      return null;
    }
  }

  async getStoreData(token, url) {
    let redirect = '';
    let currentStore = false;

    try {
      await this.storeInfoService.fetchStoreInfo();

      return new Promise((resolve, reject) => {
        this.storeList$.subscribe(
          (res) => {
            this.storeListInfo = Object.values(res);
            this.dataService.emitStoreListUpdatedEvent(res);

            if (this.targetStore) {
              this.dataService.emitLocalStorageData(this.targetStore);
              localStorage.setItem('store_id', this.targetStore.store_id.toString());
              localStorage.setItem('store_url', this.targetStore.store_url.toString());
              localStorage.setItem('store_name', this.targetStore.store_name.toString());
              this.targetStore = null;
            }
            if (this.storeListInfo.length > 0) {
              this.storeListInfo.forEach((value) => {
                if (+value.store_id === +this.defaultStore) {
                  this.defaultData = value;
                }

                if (
                  this.defaultData &&
                  Object.keys(this.defaultData).length > 0
                ) {
                  if (
                    !localStorage.getItem('store_id') ||
                    (+localStorage.getItem('store_id') === +value.store_id &&
                      !value.status)
                  ) {
                    if (
                      +localStorage.getItem('store_id') === +value.store_id &&
                      !value.status
                    ) {
                      currentStore = true;
                    }

                    this.dataService.emitLocalStorageData(this.defaultData);

                    localStorage.setItem(
                      'store_id',
                      this.defaultData.store_id.toString()
                    );
                    localStorage.setItem(
                      'store_url',
                      this.defaultData.store_url.toString()
                    );
                    localStorage.setItem(
                      'store_name',
                      this.defaultData.store_name.toString()
                    );
                  }

                  this.canInDashboard = true;

                  let info = localStorage.getItem('5s-info');
                  let parse;

                  try {
                    parse = JSON.parse(info);
                  } catch (err) {
                    parse = '';
                  }

                  redirect = parse?.redirect_to;

                  if (redirect && redirect !== 'undefined') {
                    this.loading.updateLoadingBar(false);
                    this.router.navigateByUrl(redirect);
                  } else if (currentStore && value.status) {
                    this.loading.updateLoadingBar(false);
                    window.location.reload();
                  } else if (
                    window.location.href.indexOf('/dashboard') === -1
                  ) {
                    this.loading.updateLoadingBar(false);
                    this.router.navigate(['dashboard/overview']);
                  }

                  setTimeout(() => {
                    this.loading.updateLoadingBar(false);
                    resolve(true);
                  }, 1);
                }
              });
            } else {
              reject(new Error('No stores found'));
            }
          },
          (error) => {
            this.logoutUser();
            reject(error);
          }
        );
      });
    } catch (error) {
      console.error('Error fetching dealer info:', error);
      this.logoutUser();
      throw error;
    }
  }
  logoutUser() {
    this.utils.errorUnauthorized();
  }

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.canActivate(next, state);
  }
}

@Injectable({ providedIn: 'root' })
export class CreteaNewStoreRouteGuard implements CanActivate {
  constructor(private store: Store, private router: Router) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    /*
    const stores = this.store.selectSnapshot(StoreState.storesList);
    const noStores = stores.length == 0;

    if(!noStores) {
      this.router.navigate(['/dashboard']);
    }

    return noStores;
    */
    return true;
  }
}

export interface CanComponentDeactivate {
  canDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;
}

@Injectable()
export class CanDeactivateGuard
  implements CanDeactivate<CanComponentDeactivate>
{
  canDeactivate(component: CanComponentDeactivate) {
    return component.canDeactivate ? component.canDeactivate() : true;
  }
}
